import React from 'react';
import pt from 'prop-types';

import { classNames } from 'utils';
import { APP_ACTION_HINT_DELAY } from 'constants/content';

import Icon from 'components/icon';
import Tooltip from 'components/tooltip';
import ButtonLink from 'components/button/link';
import { DropdownMenu, DropdownMenuItem } from 'components/dropdown-menu';
import AppIcon from 'components/apps/icon';
import { AppActionRunGuard } from 'components/apps/action-run';

/**
 * Bulk action buttons in a row, with the ones that don't fit in a More menu
 * that opens upward from the bottom of the page
 */
export default class CollectionBulkActions extends React.PureComponent {
  static propTypes = {
    actions: pt.array.isRequired,
    selection: pt.object.isRequired,
    onClick: pt.func,
  };

  constructor(props) {
    super(props);

    /** @type {React.RefObject<HTMLDivElement | null>} */
    this.containerRef = React.createRef();

    // Every action shows as a button, with the More button, until measured
    this.state = { numVisible: null, moreOpen: false };
  }

  componentDidMount() {
    window.addEventListener('resize', this.remeasure);
    this.measure();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.remeasure);
  }

  componentDidUpdate(prevProps) {
    const { actions, selection } = this.props;

    if (this.state.numVisible === null) {
      this.measure();
    } else if (
      // Actions show by the selection, so they change with it
      prevProps.actions !== actions ||
      prevProps.selection !== selection
    ) {
      this.remeasure();
    }
  }

  // The More menu closes, so it doesn't open by itself when its button
  // shows again
  remeasure = () => {
    this.setState({ numVisible: null, moreOpen: false });
  };

  // Counts the buttons that fit in order, before the More button when they
  // don't all fit, each with the gap that follows it. Widths are fractional,
  // as offsetWidth and clientWidth would round them
  measure() {
    const container = this.containerRef.current;
    const buttons = Array.from(container.children);
    const moreButton = buttons.pop();
    const gap = parseFloat(window.getComputedStyle(container).columnGap) || 0;
    const getWidth = (element) => element.getBoundingClientRect().width;
    const containerWidth = getWidth(container);
    const widths = buttons.map((button) => getWidth(button) + gap);

    const allWidth = widths.reduce((sum, width) => sum + width, 0) - gap;
    let numVisible = widths.length;

    if (allWidth > containerWidth) {
      let width = getWidth(moreButton);

      numVisible = widths.findIndex((buttonWidth) => {
        width += buttonWidth;
        return width > containerWidth;
      });
    }

    this.setState({ numVisible });
  }

  getVisibleActions() {
    const { actions, selection } = this.props;

    return actions.reduce((list, action, index) => {
      if (action.condition ? action.condition(selection) : true) {
        list.push({ action, index });
      }

      return list;
    }, []);
  }

  renderButton = ({ action, index }) => {
    const { onClick } = this.props;

    return (
      // An app action cannot run again while its run is active
      <AppActionRunGuard key={index} action={action}>
        {({ disabled, help }) => (
          <Tooltip
            message={disabled ? help : action.hint}
            enterDuration={disabled ? undefined : APP_ACTION_HINT_DELAY}
          >
            <ButtonLink
              size="sm"
              type={action.type}
              disabled={disabled}
              onClick={onClick}
              data-action={index}
            >
              {action.app && <AppIcon app={action.app} size={16} />}
              {action.label}
            </ButtonLink>
          </Tooltip>
        )}
      </AppActionRunGuard>
    );
  };

  onChangeMoreOpen = (moreOpen) => {
    this.setState({ moreOpen });
  };

  // The action's click handler prevents the default, which keeps the menu
  // open, so it closes here
  onClickMenuItem = (event) => {
    this.setState({ moreOpen: false });
    this.props.onClick?.(event);
  };

  // Like the items of the Actions menu. The item itself runs the action, so
  // the keyboard and a click on its padding run it too. A disabled item can
  // still be reached from the keyboard, and a click on it closes the menu
  renderMenuItem = ({ action, index }) => {
    const danger = action.type?.split(' ').includes('danger');

    return (
      <AppActionRunGuard key={index} action={action}>
        {({ disabled, help }) => (
          <DropdownMenuItem
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : this.onClickMenuItem}
            data-action={index}
          >
            {/* Beside the menu, so the hint doesn't cover the other items. The
                offset clears the item and menu padding and the menu border */}
            <Tooltip
              message={disabled ? help : action.hint}
              enterDuration={disabled ? undefined : APP_ACTION_HINT_DELAY}
              side="left"
              sideOffset={17}
            >
              <span className={classNames({ 'with-icon': action.app, danger })}>
                {action.app && <AppIcon app={action.app} size={16} />}
                {action.label}
              </span>
            </Tooltip>
          </DropdownMenuItem>
        )}
      </AppActionRunGuard>
    );
  };

  render() {
    const { numVisible, moreOpen } = this.state;
    const actions = this.getVisibleActions();
    const measuring = numVisible === null;
    const buttonActions = measuring ? actions : actions.slice(0, numVisible);
    const moreActions = measuring ? [] : actions.slice(numVisible);

    return (
      <div className="collection-bulk-actions-inner" ref={this.containerRef}>
        {buttonActions.map(this.renderButton)}

        {(measuring || moreActions.length > 0) && (
          // Sized to its items like the Actions menu, and kept off the
          // viewport edge like popovers
          <DropdownMenu
            rootRest={{
              modal: false,
              open: moreOpen,
              onOpenChange: this.onChangeMoreOpen,
            }}
            contentRest={{
              side: 'top',
              sideOffset: 12,
              align: 'start',
              collisionPadding: 8,
              style: { minWidth: 'auto' },
            }}
            triggerClassName="button button-sm button-secondary"
            triggerChildren={
              <>
                More
                <Icon fa="chevron-up" />
              </>
            }
          >
            {moreActions.map(this.renderMenuItem)}
          </DropdownMenu>
        )}
      </div>
    );
  }
}
